import { createDisplayNumberFormat } from '../../utils/displayNumberFormat';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoBarChartOutline,
  IoCalendarOutline,
  IoChevronForwardOutline,
  IoFitnessOutline,
  IoGridOutline,
} from 'react-icons/io5';
import type {
  DashboardAiAction,
  DashboardAiChartBlock,
  DashboardAiUiBlock,
} from '../../services/api';

interface Props {
  blocks: DashboardAiUiBlock[];
  athleteId?: number | null;
  onAction: (action: DashboardAiAction) => void;
}

const SERIES_COLORS = ['#ec3f83', '#5b8def', '#36c98f', '#f5b740'];

function humanize(value: string): string {
  return value.replace(/^metric\./, '').replaceAll('_', ' ');
}

function isAction(value: unknown): value is DashboardAiAction {
  return Boolean(
    value
    && typeof value === 'object'
    && 'type' in value
    && typeof (value as DashboardAiAction).type === 'string',
  );
}

function LineChart({
  block,
  athleteId,
  onAction,
}: {
  block: DashboardAiChartBlock;
  athleteId?: number | null;
  onAction: (action: DashboardAiAction) => void;
}) {
  const width = 520;
  const height = 180;
  const left = 42;
  const right = 18;
  const top = 18;
  const bottom = 34;
  const points = block.series.flatMap((series) => series.points.filter((point) => point.y != null));
  const values = points.map((point) => Number(point.y));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const range = max - min || 1;
  const labels = block.series[0]?.points.map((point) => point.x) ?? [];
  const xFor = (index: number, count: number) =>
    left + (count <= 1 ? (width - left - right) / 2 : (index / (count - 1)) * (width - left - right));
  const yFor = (value: number) => top + ((max - value) / range) * (height - top - bottom);

  return (
    <div className="dashboard-ai-ui__chart-wrap">
      <svg
        className="dashboard-ai-ui__line-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={humanize(block.title_key)}
      >
        {[0, 0.5, 1].map((step) => {
          const y = top + step * (height - top - bottom);
          return <line key={step} x1={left} x2={width - right} y1={y} y2={y} className="dashboard-ai-ui__grid-line" />;
        })}
        <text x={left - 8} y={top + 4} textAnchor="end">{max.toFixed(max < 10 ? 1 : 0)}</text>
        <text x={left - 8} y={height - bottom + 4} textAnchor="end">{min.toFixed(min < 10 ? 1 : 0)}</text>
        {block.series.map((series, seriesIndex) => {
          const valid = series.points
            .map((point, index) => ({ ...point, index }))
            .filter((point) => point.y != null);
          const path = valid
            .map((point, index) => `${index ? 'L' : 'M'} ${xFor(point.index, series.points.length)} ${yFor(Number(point.y))}`)
            .join(' ');
          return (
            <g key={series.key}>
              <path d={path} fill="none" stroke={SERIES_COLORS[seriesIndex % SERIES_COLORS.length]} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {valid.map((point) => (
                <circle
                  key={`${point.x}-${point.index}`}
                  cx={xFor(point.index, series.points.length)}
                  cy={yFor(Number(point.y))}
                  r={point.workout_id ? 5 : 3.5}
                  fill={SERIES_COLORS[seriesIndex % SERIES_COLORS.length]}
                  className={point.workout_id ? 'is-clickable' : ''}
                  onClick={point.workout_id ? () => onAction({
                    type: 'open_workout',
                    workout_id: point.workout_id,
                    date: point.x,
                    athlete_id: athleteId,
                  }) : undefined}
                >
                  <title>{`${point.x}: ${point.y}${series.unit ? ` ${series.unit}` : ''}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
        {labels.length > 0 && (
          <>
            <text x={left} y={height - 9} textAnchor="start">{labels[0]}</text>
            {labels.length > 1 && <text x={width - right} y={height - 9} textAnchor="end">{labels[labels.length - 1]}</text>}
          </>
        )}
      </svg>
    </div>
  );
}

function BarChart({ block }: { block: DashboardAiChartBlock }) {
  const { t } = useTranslation();
  return (
    <div className="dashboard-ai-ui__bar-chart">
      {block.series.map((series, index) => {
        const max = Math.max(1, ...series.points.map((point) => Number(point.y ?? 0)));
        return (
          <div className="dashboard-ai-ui__bar-series" key={series.key}>
            <div>
              <span style={{ background: SERIES_COLORS[index % SERIES_COLORS.length] }} />
              <strong>{t(`dashboard.ai.ui.${series.label_key}`, { defaultValue: humanize(series.label_key) })}</strong>
            </div>
            <div className="dashboard-ai-ui__bar-columns">
              {series.points.map((point) => (
                <div key={point.x}>
                  <strong>{point.y ?? '—'}{series.unit ? ` ${series.unit}` : ''}</strong>
                  <span className="dashboard-ai-ui__bar-track">
                    <i
                      style={{
                        height: `${Math.max(5, (Number(point.y ?? 0) / max) * 100)}%`,
                        background: SERIES_COLORS[index % SERIES_COLORS.length],
                      }}
                    />
                  </span>
                  <small>{t(`dashboard.ai.ui.${point.x}`, { defaultValue: humanize(point.x) })}</small>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DashboardAiGenerativeBlocks({ blocks, athleteId, onAction }: Props) {
  const { t, i18n } = useTranslation();
  const numberFormat = useMemo(
    () => createDisplayNumberFormat(i18n.language, { maximumFractionDigits: 2 }),
    [i18n.language],
  );
  const label = (key: string) =>
    t(`dashboard.ai.ui.${key}`, { defaultValue: humanize(key) });
  const title = (block: DashboardAiUiBlock) => {
    const params = Object.fromEntries(
      Object.entries(block.title_params ?? {}).map(([key, value]) => [
        key,
        key === 'metric' && typeof value === 'string' ? label(`metric.${value}`) : value,
      ]),
    );
    return t(`dashboard.ai.ui.${block.title_key}`, {
      defaultValue: humanize(block.title_key),
      ...params,
    });
  };
  const value = (item: string | number | null | DashboardAiAction) => {
    if (item == null) return '—';
    if (typeof item === 'number') return numberFormat.format(item);
    if (typeof item === 'string') return label(item) === humanize(item) ? item : label(item);
    return '';
  };

  if (!blocks.length) return null;

  return (
    <div className="dashboard-ai-ui" aria-label={t('dashboard.ai.ui.generatedResponse')}>
      {blocks.map((block) => {
        if (block.type === 'metric_grid') {
          return (
            <section className="dashboard-ai-ui__block" key={block.id}>
              <header><IoGridOutline aria-hidden /><strong>{title(block)}</strong></header>
              <div className="dashboard-ai-ui__metrics">
                {block.items.filter((item) => item.value != null).map((item) => (
                  <div className={`is-${item.tone ?? 'neutral'}`} key={item.key}>
                    <small>{label(item.key)}</small>
                    <strong>{value(item.value)}{item.unit ? <em>{item.unit}</em> : null}</strong>
                  </div>
                ))}
              </div>
            </section>
          );
        }

        if (block.type === 'chart') {
          return (
            <section className="dashboard-ai-ui__block" key={block.id}>
              <header><IoBarChartOutline aria-hidden /><strong>{title(block)}</strong></header>
              {block.chart_type === 'bar'
                ? <BarChart block={block} />
                : <LineChart block={block} athleteId={athleteId} onAction={onAction} />}
              <div className="dashboard-ai-ui__legend">
                {block.series.map((series, index) => (
                  <span key={series.key}>
                    <i style={{ background: SERIES_COLORS[index % SERIES_COLORS.length] }} />
                    {label(series.label_key)}
                    {series.unit ? ` (${series.unit})` : ''}
                  </span>
                ))}
              </div>
            </section>
          );
        }

        if (block.type === 'table') {
          return (
            <section className="dashboard-ai-ui__block" key={block.id}>
              <header><IoGridOutline aria-hidden /><strong>{title(block)}</strong></header>
              <div className="dashboard-ai-ui__table-wrap">
                <table>
                  <thead>
                    <tr>{block.columns.map((column) => <th key={column.key}>{label(column.label_key)}</th>)}</tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, index) => {
                      const rowAction = isAction(row._action) ? row._action : null;
                      return (
                        <tr
                          key={`${block.id}-${index}`}
                          className={rowAction ? 'is-clickable' : ''}
                          onClick={rowAction ? () => onAction(rowAction) : undefined}
                        >
                          {block.columns.map((column) => <td key={column.key}>{value(row[column.key])}</td>)}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        }

        return (
          <section className="dashboard-ai-ui__block" key={block.id}>
            <header><IoFitnessOutline aria-hidden /><strong>{title(block)}</strong></header>
            <div className="dashboard-ai-ui__workouts">
              {block.items.map((workout) => {
                const workoutAction = workout.action ?? {
                  type: 'open_workout' as const,
                  workout_id: workout.workout_id,
                  date: workout.date,
                  athlete_id: athleteId,
                };
                return (
                  <button type="button" key={workout.workout_id} onClick={() => onAction(workoutAction)}>
                    <span className="dashboard-ai-ui__workout-icon"><IoCalendarOutline aria-hidden /></span>
                    <span>
                      <strong>{workout.title}</strong>
                      <small>{[workout.date, workout.sport].filter(Boolean).join(' · ')}</small>
                    </span>
                    <span className="dashboard-ai-ui__workout-values">
                      {workout.metric_value != null && (
                        <strong>{numberFormat.format(workout.metric_value)} {workout.metric_unit}</strong>
                      )}
                      {workout.metric_value == null && workout.tss != null && <strong>TSS {numberFormat.format(workout.tss)}</strong>}
                      {workout.duration_seconds != null && <small>{Math.round(workout.duration_seconds / 60)} min</small>}
                    </span>
                    <IoChevronForwardOutline aria-hidden />
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
